import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardSubtitle,
  CCardTitle,
  CCol,
  CForm,
  CFormInput,
  CRow,
} from '@coreui/react-pro'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useProfileForm from 'src/hooks/forms/profile/useProfileForm'
import ProfileFormValues from 'src/types/forms/profile/ProfileFormValues'
import styles from '../../views/Profile/profile.module.scss'
import ChangePasswordModal from '../ChangePasswordModal/ChangePasswordModal'
import UnstyledButton from '../UnstyledButton/UnstyledButton'

const tDetails = 'profile.details'
const tForm = `${tDetails}.form`

interface ProfileFormProps {
  defaultValues?: Partial<ProfileFormValues>
  onSubmit: () => Promise<void>
  onCancel?: () => void
  hideTitles?: boolean
}

const ProfileForm = ({
  defaultValues = {},
  onSubmit,
  onCancel,
  hideTitles = false,
}: ProfileFormProps) => {
  const { t } = useTranslation()

  const {
    register,
    reset,
    formState: { errors, isSubmitting, isLoading: formIsLoading },
    handleSubmit,
  } = useProfileForm(defaultValues)

  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false)

  const openChangePasswordModal = () => {
    setChangePasswordModalOpen(true)
  }

  const closeChangePasswordModal = () => {
    setChangePasswordModalOpen(false)
  }

  const handleCancel = () => {
    reset(defaultValues)
    onCancel?.()
  }

  const getUserFullName = () => {
    const firstName = defaultValues?.firstName || ''
    const lastName = defaultValues?.lastName || ''
    return `${firstName} ${lastName}`.trim() || t(`${tDetails}.title`)
  }

  const titlesEl = (
    <div className="vstack gap-1">
      <CCardTitle className={styles.title}>{getUserFullName()}</CCardTitle>
      <CCardSubtitle className={styles.subtitle}>
        {t(`${tDetails}.updateInformation`)}
      </CCardSubtitle>
    </div>
  )

  const passwordResetEl = (
    <UnstyledButton onClick={openChangePasswordModal} type="button">
      <CAlert color="light">
        <span style={{ textDecoration: 'underline' }}>{t(`${tDetails}.passwordReset`)}</span>
      </CAlert>
    </UnstyledButton>
  )

  const submitLabel = t(`buttons.${isSubmitting ? 'savingChanges' : 'saveChanges'}`)
  const submitDisabled = formIsLoading || isSubmitting

  const buttonsEl = (
    <div className="hstack gap-2 justify-content-end">
      <CButton
        type="button"
        variant="outline"
        color="primary"
        onClick={handleCancel}
        disabled={formIsLoading || isSubmitting}
      >
        {t('buttons.cancel')}
      </CButton>
      <CButton type="submit" color="primary" disabled={submitDisabled}>
        {submitLabel}
      </CButton>
    </div>
  )

  const userDataFieldsEl = (
    <div className="vstack gap-3">
      <CRow>
        <CCol>
          <CFormInput
            id="firstName"
            label={`${t(`${tForm}.firstName.label`)} *`}
            invalid={!!errors?.firstName}
            feedbackInvalid={errors?.firstName?.message}
            {...register('firstName')}
          />
        </CCol>
        <CCol>
          <CFormInput
            id="lastName"
            label={`${t(`${tForm}.lastName.label`)} *`}
            invalid={!!errors?.lastName}
            feedbackInvalid={errors?.lastName?.message}
            {...register('lastName')}
          />
        </CCol>
      </CRow>
      <CRow>
        <CCol>
          <CFormInput
            id="position"
            label={`${t(`${tForm}.position.label`)} *`}
            invalid={!!errors?.position}
            feedbackInvalid={errors?.position?.message}
            {...register('position')}
          />
        </CCol>
      </CRow>
    </div>
  )

  const contactsFieldsEl = (
    <div className="vstack gap-3">
      <CRow>
        <CCol>
          <CFormInput
            id="officeNumber"
            label={`${t(`${tForm}.officeNumber.label`)} *`}
            invalid={!!errors?.officeNumber}
            feedbackInvalid={errors?.officeNumber?.message}
            {...register('officeNumber')}
          />
        </CCol>
        <CCol>
          <CFormInput
            id="mobileNumber"
            label={t(`${tForm}.mobileNumber`)}
            invalid={!!errors?.mobileNumber}
            feedbackInvalid={errors?.mobileNumber?.message}
            {...register('mobileNumber')}
          />
        </CCol>
      </CRow>
      <CRow>
        <CCol>
          <CFormInput
            id="email"
            label={`${t(`${tForm}.email.label`)} *`}
            invalid={!!errors?.email}
            feedbackInvalid={errors?.email?.message}
            {...register('email')}
          />
        </CCol>
      </CRow>
    </div>
  )

  const formEl = (
    <CForm className="vstack gap-4" onSubmit={handleSubmit(onSubmit)}>
      <CCard className="form">{userDataFieldsEl}</CCard>
      <div className={styles.contactsSection}>{t('contacts.title')}</div>
      <CCard className="form">{contactsFieldsEl}</CCard>
      {passwordResetEl}
      {buttonsEl}
    </CForm>
  )

  const changePasswordModalEl = (
    <ChangePasswordModal open={changePasswordModalOpen} onClose={closeChangePasswordModal} />
  )

  const contentEl = (
    <CCard>
      <CCardBody className="vstack gap-4 px-0">
        {!hideTitles && titlesEl}
        {formEl}
      </CCardBody>
    </CCard>
  )

  return (
    <>
      {contentEl}
      {changePasswordModalEl}
    </>
  )
}

export default ProfileForm
